import { AppConfig, BrandAppConfig } from "@/redux/types";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { getAppConfig, getBrandAppConfigs } from "../../bootstrap";
import { getBrandAppConfigsLegacyDoNotUse } from "../rest/AppConfig";
import { CachedQueryKeys } from "./cachedQueryKeys";
import { THIRTY_MINUTES_IN_MS } from "./constants";
import { BCQueryOptions } from "./types";

export const brandAppConfigQueryOptions = () => {
  return queryOptions({
    queryKey: [CachedQueryKeys.BrandAppConfigs],
    queryFn: ({ signal }) => getBrandAppConfigs(signal),
    // since config data rarely changes, we can use the cached data for longer and refresh it every 30 minutes
    staleTime: THIRTY_MINUTES_IN_MS,
  });
};

export const appConfigQueryOptions = () => {
  return queryOptions({
    queryKey: [CachedQueryKeys.AppConfig],
    queryFn: ({ signal }) => getAppConfig(signal),
    // since config data rarely changes, we can use the cached data for longer and refresh it every 30 minutes
    staleTime: THIRTY_MINUTES_IN_MS,
  });
};

export const useAppConfig = <TSelect = AppConfig>(
  args?: BCQueryOptions<typeof appConfigQueryOptions, AppConfig, TSelect>
) => {
  return useQuery({
    ...appConfigQueryOptions(),
    ...args,
    select: args?.select,
  });
};

export const useDomainConfig = (
  args?: BCQueryOptions<typeof appConfigQueryOptions, AppConfig, AppConfig>
) => {
  const { data: appConfig, ...rest } = useAppConfig<AppConfig>(args);
  return {
    data: appConfig?.domainConfig,
    ...rest,
  };
};

export const useBrandAppConfig = <TSelect = BrandAppConfig>(
  args?: BCQueryOptions<
    typeof brandAppConfigQueryOptions,
    BrandAppConfig,
    TSelect
  >
) => {
  return useQuery({
    ...brandAppConfigQueryOptions(),
    ...args,
    select: args?.select,
  });
};

/**
 * Temporary fallback query that uses the legacy endpoint when the new one fails.
 * The new endpoint relies on hostname headers that may not match the origin.
 * To be removed once fixed, as the legacy endpoint creates a slower request waterfall.
 *
 * @deprecated please use useBrandAppConfig once it's confirmed the new endpoint is stable
 */
export const useBrandAppConfigWithFallback = <TSelect = BrandAppConfig>(
  args?: BCQueryOptions<
    typeof brandAppConfigQueryOptions,
    BrandAppConfig,
    TSelect
  >
) => {
  const { data: domainConfig } = useDomainConfig({
    notifyOnChangeProps: args?.notifyOnChangeProps,
  });
  const brandAppConfigQueryResult = useBrandAppConfig();
  const fallBackQueryResult = useQuery({
    ...brandAppConfigQueryOptions(),
    queryFn: ({ signal }) => {
      if (!domainConfig?.domainName) {
        throw new Error("Domain name is required to fetch brand app configs");
      }
      return getBrandAppConfigsLegacyDoNotUse(domainConfig.domainName, signal);
    },
    enabled: !!domainConfig?.domainName && brandAppConfigQueryResult.isError,
    ...args,
    select: args?.select,
  });

  if (brandAppConfigQueryResult.isError) {
    return fallBackQueryResult;
  }

  return brandAppConfigQueryResult;
};
